import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Button, Paper, alpha, IconButton, Avatar } from '@mui/material';
import { ArrowBack, Groups, ArrowLeft, ArrowRight } from '@mui/icons-material';
import heroImage from '../../assets/Text-WFHCafe_WObg.png';

interface CommunityHeaderProps {
  handleBack: () => void;
  isDark: boolean;
  goldColor: string;
  glassEffect: any;
  communities?: Array<{
    id: string;
    name: string;
    slug: string;
    description?: string;
    profilePicture?: string;
    bannerImage?: string;
    membersCount?: number;
    postsCount?: number;
    members?: Array<{
      id: string;
      profilePicture?: string;
      username?: string;
    }>;
  }>;
}

const CommunityHeader: React.FC<CommunityHeaderProps> = ({
  handleBack,
  isDark,
  goldColor,
  glassEffect,
  communities = []
}) => {
  const deepGreen = '#051814';
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const slideInterval = useRef<NodeJS.Timeout | null>(null);
  
  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  // Filter communities with banner images
  const featuredCommunities = communities.filter(community => 
    community.bannerImage
  );

  // Memoize the auto-slide function to prevent recreation on every render
  const startAutoSlide = useCallback(() => {
    if (slideInterval.current) {
      clearInterval(slideInterval.current);
    }
    
    slideInterval.current = setInterval(() => {
      if (featuredCommunities.length > 0) {
        setCurrentSlide(prev => (prev + 1) % featuredCommunities.length);
      }
    }, 5000); // 5 seconds
  }, [featuredCommunities.length]);

  const stopAutoSlide = useCallback(() => {
    if (slideInterval.current) {
      clearInterval(slideInterval.current);
      slideInterval.current = null;
    }
  }, []);

  // Setup and cleanup auto-slide
  useEffect(() => {
    if (featuredCommunities.length > 0 && isAutoPlaying) {
      startAutoSlide();
    }
    
    return () => {
      stopAutoSlide();
    };
  }, [featuredCommunities.length, isAutoPlaying, startAutoSlide, stopAutoSlide]);

  const handlePrev = () => {
    stopAutoSlide();
    setCurrentSlide(prev => (prev - 1 + featuredCommunities.length) % featuredCommunities.length);
    
    // Restart auto-slide after 5 seconds
    if (isAutoPlaying) {
      const restartTimeout = setTimeout(() => {
        startAutoSlide();
      }, 5000);
      
      // Cleanup timeout on unmount
      return () => clearTimeout(restartTimeout);
    }
  };

  const handleNext = () => {
    stopAutoSlide();
    setCurrentSlide(prev => (prev + 1) % featuredCommunities.length);
    
    // Restart auto-slide after 5 seconds
    if (isAutoPlaying) {
      const restartTimeout = setTimeout(() => {
        startAutoSlide();
      }, 5000);
      
      // Cleanup timeout on unmount
      return () => clearTimeout(restartTimeout);
    }
  };

  const handleDotClick = (index: number) => {
    stopAutoSlide();
    setCurrentSlide(index);
    
    // Restart auto-slide after 5 seconds
    if (isAutoPlaying) {
      const restartTimeout = setTimeout(() => {
        startAutoSlide();
      }, 5000);
      
      // Cleanup timeout on unmount
      return () => clearTimeout(restartTimeout);
    }
  };

  const handleCommunityClick = (slug: string) => {
    window.location.href = `/community/${slug}`;
  };

  const handleJoinClick = (e: React.MouseEvent, slug: string) => {
    e.stopPropagation(); // Prevent triggering the main click handler
    window.location.href = `/community/${slug}`;
  };

  // If no featured communities, show default header
  if (featuredCommunities.length === 0) {
    return (
      <>
        {/* <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mb: 3, fontSize: '1.3rem', height: '40px', color: goldColor, '&:hover': { background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)' } }}
        >
          Back
        </Button> */}

        <Paper sx={{
          p: 0,
          mb: 4,
          borderRadius: '20px',
          overflow: 'hidden',
          ...glassEffect,
          display: 'flex',
          flexDirection: { xs: 'column-reverse', md: 'row' },
          alignItems: 'center',
          height: 250 
        }}>
          <Box sx={{ p: { xs: 4, md: 6 }, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Groups sx={{ fontSize: 40, color: goldColor, mr: 2 }} />
              <Typography variant="h3" sx={{
                ...discordFont,
                background: isDark ? 'linear-gradient(135deg, #C4B66F 0%, #FFFFFF 100%)' : `linear-gradient(135deg, ${deepGreen} 0%, ${deepGreen} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                fontSize: { xs: '2rem', md: '3rem' }
              }}>
                wfh/Community
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(5, 24, 20, 0.8)', fontWeight: 400, maxWidth: 600 }}>
              Join communities of remote workers, cafe lovers, and digital nomads. Share experiences, find collaborators, or create your own space.
            </Typography>
          </Box>
          <Box sx={{
            width: { xs: '100%', md: '40%' },
            height: { xs: 200, md: '100%' },
            minHeight: { md: 250 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 2, md: 4 }
          }}>
            <img 
              src={heroImage} 
              alt="Community Hero" 
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain', 
                maxHeight: 250 
              }} 
            />
          </Box>
        </Paper>
      </>
    );
  }

  // Show carousel when there are featured communities
  const currentCommunity = featuredCommunities[currentSlide];

  return (
    <>
      {/* <Button
        startIcon={<ArrowBack />}
        onClick={handleBack}
        sx={{ mb: 3, fontSize: '1.3rem', height: '40px', color: goldColor, '&:hover': { background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)' } }}
      >
        Back
      </Button> */}

      <Paper sx={{
        p: 0,
        mb: 4,
        borderRadius: '20px',
        overflow: 'hidden',
        ...glassEffect,
        position: 'relative',
        height: 320
      }}>
        {/* Community Banner */}
        <Box 
          onClick={() => handleCommunityClick(currentCommunity.slug)}
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            backgroundImage: currentCommunity.bannerImage 
              ? `url(${currentCommunity.bannerImage})`
              : (isDark
                  ? 'linear-gradient(135deg, #C4B66F 0%, #0F3B2E 100%)'
                  : 'linear-gradient(135deg, #051814 0%, #1D5C4A 100%)'),
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            cursor: 'pointer',
            '&:hover': {
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.1)',
              }
            }
          }}
        >
          {/* Gradient overlay for better text readability */}
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '60%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)'
          }} />
          
          {/* Community Info at Bottom Left */}
          <Box sx={{
            position: 'absolute',
            bottom: 30,
            left: 30,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 3,
            zIndex: 2
          }}>
            {/* Community Profile Picture and Name */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={currentCommunity.profilePicture}
                sx={{
                  width: 75,
                  height: 75,
                  border: `3px solid ${goldColor}`,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                  bgcolor: goldColor,
                  color: deepGreen,
                  fontWeight: 700,
                  fontSize: '1.5rem'
                }}
              >
                {currentCommunity.name?.charAt(0)?.toUpperCase() || 'C'}
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{
                  ...discordFont,
                  color: '#fff',
                  fontSize: '2rem',
                  mb: 0.5,
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                }}>
                  wfh/{currentCommunity.slug}
                </Typography>
                <Typography variant="body1" sx={{
                  color: 'rgba(255,255,255,0.9)',
                  maxWidth: 500,
                  fontSize: '1rem',
                  textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                }}>
                  {currentCommunity.description || 'Join this community to connect with like-minded people'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Join Button at Bottom Right */}
          <Box sx={{
            position: 'absolute',
            bottom: 30,
            right: 30,
            zIndex: 2
          }}>
            <Button
              variant="contained"
              onClick={(e) => handleJoinClick(e, currentCommunity.slug)}
              sx={{
                background: 'transparent',
                boxShadow: 'none',
                color: goldColor,
                borderRadius: '25px',
                fontWeight: 800,
                fontSize: '1rem',
                textTransform: 'none',
                padding: '5px 10px',
                border: `2px solid ${alpha(goldColor, 0.6)}`,
                backdropFilter: 'blur(20px)',
                '&:hover': {
                  background: 'transparent',
                  boxShadow: 'none',
                  borderColor: goldColor,
                },
              }}
            >
              Join Community
            </Button>
          </Box>

          {/* Navigation Arrows */}
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handlePrev();
            }}
            sx={{
              position: 'absolute',
              left: 5,
              top: '50%',
              transform: 'translateY(-50%)',
              color: goldColor,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.4)',
              },
              zIndex: 3,
            }}
          >
            <ArrowLeft fontSize="large" sx={{ 
              fontSize: '2.5rem', 
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' 
            }} />
          </IconButton>

          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            sx={{
              position: 'absolute',
              right: 5,
              top: '50%',
              transform: 'translateY(-50%)',
              color: goldColor,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.4)',
              },
              zIndex: 3,
            }}
          >
            <ArrowRight fontSize="large" sx={{ 
              fontSize: '2.5rem', 
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' 
            }} />
          </IconButton>

          {/* Slide Indicators */}
          <Box sx={{
            position: 'absolute',
            bottom: 15,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            zIndex: 3
          }}>
            {featuredCommunities.map((_, index) => (
              <Box
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDotClick(index);
                }}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: index === currentSlide ? goldColor : alpha(goldColor, 0.3),
                  cursor: 'pointer',
                  transition: 'background-color 0.3s, transform 0.2s',
                  '&:hover': {
                    backgroundColor: index === currentSlide ? goldColor : alpha(goldColor, 0.5),
                    transform: 'scale(1.2)'
                  }
                }}
              />
            ))}
          </Box>
        </Box>
      </Paper>
    </>
  );
};

export default CommunityHeader;